/**
 * SpeakersTab
 * ──────────────
 * Manage speakers embedded in event.speakers[] (speakerSchema).
 * Saved via PUT /events/:id  { speakers: [...] }
 *
 * Features:
 *  - Speaker card grid (fixed-height cards, horizontal layout)
 *  - Add / Edit / Delete via right-side drawer
 *  - Auto-save on change (debounced 1s, snapshot-guarded)
 *  - Avatar upload + social links
 */
import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import {
  Box, Typography, Button, Card, IconButton, TextField,
  Alert, Drawer, Avatar, Chip, Tooltip, Grid, Skeleton,
} from '@mui/material';
import {
  AddOutlined, EditOutlined, DeleteOutlined, CloseOutlined,
  SaveOutlined, RecordVoiceOverOutlined, PhotoCameraOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../../../hooks/useApi';
import { v4 as uuid } from 'uuid';
import { PRIMARY, INPUT_SX, FormField } from '../shared';
import { ContentBrowserModal } from '../lobbyBuilderTab/ContentBrowserModal';

const BLANK = {
  name: '', title: '', company: '', bio: '', avatar: '',
  track: '', order: 0,
  socialLinks: { linkedin: '', twitter: '', website: '' },
};

// Portrait ratio used for both the card banner and the content browser crop
const PORTRAIT_RATIO = 3.5 / 4; // width / height → taller than wide

// URL validation helper
function isValidUrl(str) {
  if (!str) return true; // empty is fine
  try { new URL(str); return true; } catch { return false; }
}


// ── Brand icon components (MUI doesn't ship these) ───────────────────────────
function LinkedInIcon({ size = 20 }) {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: size, height: size, display: 'block' }} fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </Box>
  );
}

function XIcon({ size = 20 }) {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: size, height: size, display: 'block' }} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </Box>
  );
}

function WebsiteIcon({ size = 20 }) {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: size, height: size, display: 'block' }} fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
    </Box>
  );
}

// ── Speaker drawer form ───────────────────────────────────────────────────────
export function SpeakerDrawer({ open, speaker, onClose, onSave }) {
  const [form, setForm]         = useState({ ...BLANK });
  const [photoBrowser, setPhotoBrowser] = useState(false);
  const [urlErrors, setUrlErrors]       = useState({});

  useEffect(() => {
    if (speaker) setForm({ id: uuid(), ...BLANK, ...speaker, socialLinks: { linkedin: '', twitter: '', website: '', ...(speaker.socialLinks || {}) } });
    else         setForm({ id: uuid(), ...BLANK });
    setUrlErrors({});
  }, [speaker, open]);

  const set       = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setSocial = (k, v) => {
    setForm(p => ({ ...p, socialLinks: { ...p.socialLinks, [k]: v } }));
    setUrlErrors(e => ({ ...e, [k]: v && !isValidUrl(v) }));
  };

  const hasUrlError = Object.values(urlErrors).some(Boolean);
  const canSubmit   = form.name.trim() && !hasUrlError;

  const hue = (form.name?.charCodeAt(0) || 65) * 13 % 360;

  return (
    <>
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 460 }, display: 'flex', flexDirection: 'column' } }}>

      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {speaker ? 'Edit Speaker' : 'Add Speaker'}
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseOutlined sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>

        {/* Portrait photo picker */}
        <FormField label="Photo">
          <Box
            onClick={() => setPhotoBrowser(true)}
            sx={{
              width: '100%',
              aspectRatio: '3.5/4',
              borderRadius: 2,
              overflow: 'hidden',
              border: '2px dashed #d1d9e6',
              cursor: 'pointer',
              position: 'relative',
              // background: form.avatar
              //   ? 'transparent'
              //   : `linear-gradient(145deg, hsl(${hue},55%,96%) 0%, hsl(${hue},40%,90%) 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'border-color 0.15s',
              '&:hover': { borderColor: PRIMARY },
              '&:hover .photo-overlay': { opacity: 1 },
            }}
          >
            {form.avatar ? (
              <Box component="img" src={form.avatar} alt="Speaker photo"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: '#9ea8b3' }}>
                <PhotoCameraOutlined sx={{ fontSize: 32 }} />
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Click to upload photo</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#b0bac8' }}>400 x 460 recommended</Typography>
              </Box>
            )}
            {/* Hover overlay */}
            <Box className="photo-overlay" sx={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 0.5,
              background: 'rgba(0,0,0,0.45)', opacity: 0, transition: 'opacity 0.15s',
            }}>
              <PhotoCameraOutlined sx={{ color: '#fff', fontSize: 28 }} />
              <Typography sx={{ color: '#fff', fontSize: '0.75rem', fontWeight: 600 }}>Change photo</Typography>
            </Box>
          </Box>
        </FormField>

        <FormField label="Full Name" required>
          <TextField value={form.name} onChange={e => set('name', e.target.value)}
            fullWidth size="small" placeholder="Jane Doe" sx={INPUT_SX} />
        </FormField>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <FormField label="Title / Role">
              <TextField value={form.title || ''} onChange={e => set('title', e.target.value)}
                fullWidth size="small" placeholder="CEO" sx={INPUT_SX} />
            </FormField>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormField label="Company">
              <TextField value={form.company || ''} onChange={e => set('company', e.target.value)}
                fullWidth size="small" placeholder="GoVirtual Inc." sx={INPUT_SX} />
            </FormField>
          </Box>
        </Box>

        <FormField label="Bio">
          <TextField value={form.bio || ''} onChange={e => set('bio', e.target.value)}
            fullWidth size="small" multiline rows={3}
            placeholder="Short speaker biography..." sx={INPUT_SX} />
        </FormField>

        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1.5, mt: 0.5, fontSize: '0.875rem' }}>
          Social Links
        </Typography>

        <FormField label="LinkedIn">
          <TextField
            value={form.socialLinks.linkedin || ''}
            onChange={e => setSocial('linkedin', e.target.value)}
            fullWidth size="small" placeholder="https://linkedin.com/in/..."
            error={!!urlErrors.linkedin}
            helperText={urlErrors.linkedin ? 'Enter a valid URL' : ''}
            sx={INPUT_SX}
          />
        </FormField>
        <FormField label="Twitter / X">
          <TextField
            value={form.socialLinks.twitter || ''}
            onChange={e => setSocial('twitter', e.target.value)}
            fullWidth size="small" placeholder="https://twitter.com/..."
            error={!!urlErrors.twitter}
            helperText={urlErrors.twitter ? 'Enter a valid URL' : ''}
            sx={INPUT_SX}
          />
        </FormField>
        <FormField label="Website">
          <TextField
            value={form.socialLinks.website || ''}
            onChange={e => setSocial('website', e.target.value)}
            fullWidth size="small" placeholder="https://..."
            error={!!urlErrors.website}
            helperText={urlErrors.website ? 'Enter a valid URL' : ''}
            sx={INPUT_SX}
          />
        </FormField>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e8edf2', flexShrink: 0, background: '#fafbfc' }}>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button
            onClick={() => onSave(form)}
            variant="contained" startIcon={<SaveOutlined />}
            disabled={!canSubmit}
            sx={{ borderRadius: 2, background: PRIMARY, '&:hover': { background: '#0d5fa8' } }}
          >
            Save Speaker
          </Button>
        </Box>
      </Box>
    </Drawer>

    {/* Content browser — portrait crop enforced */}
    <ContentBrowserModal
      open={photoBrowser}
      onClose={() => setPhotoBrowser(false)}
      onSelect={url => { set('avatar', url); setPhotoBrowser(false); }}
      mediaType="image"
      currentUrl={form.avatar || ''}
      forcedAspectRatio={PORTRAIT_RATIO}
    />
    </>
  );
}

// ── Speaker card ──────────────────────────────────────────────────────────────
function SpeakerCard({ speaker, onEdit, onDelete }) {
  const initials  = speaker.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const hue       = (speaker.name?.charCodeAt(0) || 65) * 13 % 360;
  const hasSocial = speaker.socialLinks?.linkedin || speaker.socialLinks?.twitter || speaker.socialLinks?.website;

  return (
    <Card onClick={() => onEdit(speaker)} sx={{
      borderRadius: 1,
      border: '1px solid #e8edf2',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      width: '100%',
      height: '100%',
      cursor: 'pointer',
      transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
      '&:hover': {
        boxShadow: '0 6px 24px rgba(0,0,0,0.10)',
        borderColor: alpha(PRIMARY, 0.3),
        '& .spk-actions': { opacity: 1 },
      },
    }}>

      {/* ── Photo banner — portrait ratio (3:4) ── */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '3.5/4',
        flexShrink: 0,
        background: `linear-gradient(145deg, hsl(${hue},60%,92%) 0%, hsl(${hue},45%,80%) 100%)`,
        overflow: 'hidden',
      }}>
        {/* Delete button — fades in on hover, stops click from bubbling to card */}
        <Box className="spk-actions" sx={{
          position: 'absolute', top: 8, right: 8, zIndex: 2,
          opacity: 0, transition: 'opacity 0.15s ease',
        }}>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={e => { e.stopPropagation(); onDelete(speaker); }}
              sx={{
                background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)',
                p: 0.75, '&:hover': { background: '#fff', color: '#d32f2f' },
              }}
            >
              <DeleteOutlined sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Full-cover photo or centred initials avatar */}
        {speaker.avatar ? (
          <Box component="img" src={speaker.avatar} alt={speaker.name} sx={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%', objectFit: 'cover',
          }} />
        ) : (
          <Avatar sx={{
            position: 'absolute',
            top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 72, height: 72, fontSize: '1.6rem', fontWeight: 700,
            background: `hsl(${hue}, 58%, 46%)`,
            border: '3px solid rgba(255,255,255,0.7)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>{initials}</Avatar>
        )}

        {/* Gradient scrim */}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.18), transparent)',
          pointerEvents: 'none',
        }} />
      </Box>

      {/* ── Content ── */}
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', textAlign: 'center',
        px: 1.5, pt: 2, pb: 2,
        gap: 0.5,
      }}>

        <Typography sx={{
          fontWeight: 800, fontSize: '1rem', color: '#1a1a2e', lineHeight: 1.25,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}>
          {speaker.name}
        </Typography>

        {(speaker.title || speaker.company) && (
          <Typography sx={{
            fontSize: '0.73rem', color: '#6b7280', lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}>
            {[speaker.title, speaker.company].filter(Boolean).join(' · ')}
          </Typography>
        )}

        {speaker.track && (
          <Chip label={speaker.track} size="small" sx={{
            mt: 0.25, height: 18, fontSize: '0.63rem', fontWeight: 600,
            background: alpha(PRIMARY, 0.08), color: PRIMARY,
            maxWidth: '90%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
          }} />
        )}

        {/* Bio — 2-line preview, always reserves 2 lines for alignment */}
        <Typography sx={{
          mt: 0.5, fontSize: '0.71rem', color: '#9ea8b3', lineHeight: 1.5,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          minHeight: '2.1em',
        }}>
          {speaker.bio || ''}
        </Typography>

        {/* Social icons — stop propagation so they don't open the edit drawer */}
        {hasSocial && (
          <Box
            onClick={e => e.stopPropagation()}
            sx={{ mt: 'auto', pt: 1, display: 'flex', gap: 0.25, justifyContent: 'center' }}
          >
            {speaker.socialLinks?.linkedin && (
              <Tooltip title="LinkedIn">
                <IconButton size="small" component="a" href={speaker.socialLinks.linkedin} target="_blank"
                  sx={{ color: '#0077b5', p: 0.5 }}>
                  <LinkedInIcon size={20} />
                </IconButton>
              </Tooltip>
            )}
            {speaker.socialLinks?.twitter && (
              <Tooltip title="Twitter / X">
                <IconButton size="small" component="a" href={speaker.socialLinks.twitter} target="_blank"
                  sx={{ color: '#000', p: 0.5 }}>
                  <XIcon size={20} />
                </IconButton>
              </Tooltip>
            )}
            {speaker.socialLinks?.website && (
              <Tooltip title="Website">
                <IconButton size="small" component="a" href={speaker.socialLinks.website} target="_blank"
                  sx={{ color: '#5a6474', p: 0.5 }}>
                  <WebsiteIcon size={20} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const SpeakersTab = forwardRef(function SpeakersTab({ event, onSave }, ref) {
  const [speakers, setSpeakers]     = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');
  const [saved, setSaved]           = useState(false);
  const [ready, setReady]           = useState(false);

  const autoSaveTimer  = useRef(null);
  const serverSnapshot = useRef(null);

  // Hydrate from event prop — capture snapshot so auto-save doesn't fire on load
  useEffect(() => {
    const loaded = event?.speakers ? [...event.speakers] : [];
    serverSnapshot.current = JSON.stringify(loaded);
    setSpeakers(loaded);
    setReady(true);
  }, [event]);

  const openAdd  = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (s) => { setEditing(s); setDrawerOpen(true); };

  const handleSaveSpeaker = useCallback((form) => {
    setSpeakers(prev => {
      if (editing) return prev.map(s => s.name === editing.name ? form : s);
      return [...prev, form];
    });
    setDrawerOpen(false);
  }, [editing]);

  const handleDelete = (speaker) => {
    setSpeakers(prev => prev.filter(s => s.name !== speaker.name));
  };

  const handleSaveAll = useCallback(async (currentSpeakers) => {
    if (!event?._id) return;
    const payload = currentSpeakers ?? speakers;
    setSaving(true); setSaveError('');
    try {
      await api.put(`/events/${event._id}`, { speakers: payload });
      serverSnapshot.current = JSON.stringify(payload); // update snapshot after save
      setSaved(true); onSave?.();
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setSaveError(e?.response?.data?.message || 'Failed to save speakers');
    } finally { setSaving(false); }
  }, [event, speakers, onSave]);

  // Auto-save: debounce 1s after any speakers change, snapshot-guarded
  useEffect(() => {
    if (serverSnapshot.current === null) return;
    if (JSON.stringify(speakers) === serverSnapshot.current) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => handleSaveAll(speakers), 1000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [speakers]); // eslint-disable-line

  useImperativeHandle(ref, () => ({
    save: () => handleSaveAll(speakers),
    get saving() { return saving; },
    get saved()  { return saved;  },
  }), [handleSaveAll, saving, saved, speakers]);

  // ── Loading skeleton ─────────────────────────────────────────────────────────
  if (!ready) return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Skeleton width={180} height={30} />
        <Skeleton variant="rectangular" width={110} height={34} sx={{ borderRadius: 2 }} />
      </Box>
      <Grid container spacing={2}>
        {[...Array(8)].map((_, i) => (
          <Grid item xs={6} sm={4} md={3} key={i}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e8edf2', boxShadow: 'none', overflow: 'hidden' }}>
              <Skeleton variant="rectangular" sx={{ width: '100%', aspectRatio: '3.5/4' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, pt: 5, pb: 2, gap: 1 }}>

                <Skeleton width="65%" height={22} />
                <Skeleton width="50%" height={14} />
                <Skeleton width="80%" height={12} />
                <Skeleton width="70%" height={12} />
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box>
      {saveError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSaveError('')}>{saveError}</Alert>}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1a1a2e' }}>
          Speakers
          <Chip label={speakers.length} size="small" sx={{ ml: 1, height: 20, fontSize: '0.72rem', background: alpha(PRIMARY, 0.08), color: PRIMARY }} />
        </Typography>
        <Button size="small" startIcon={<AddOutlined />} variant="outlined" onClick={openAdd}
          sx={{ borderRadius: 2, textTransform: 'none' }}>
          Add Speaker
        </Button>
      </Box>

      {speakers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <RecordVoiceOverOutlined sx={{ fontSize: 48, color: alpha(PRIMARY, 0.15), mb: 2 }} />
          <Typography sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>No speakers added yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add speakers who will be presenting at this event.
          </Typography>
          <Button variant="contained" startIcon={<AddOutlined />} onClick={openAdd}
            sx={{ borderRadius: 2, background: PRIMARY }}>
            Add First Speaker
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2} alignItems="stretch">
          {speakers
            .slice()
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((s, i) => (
              <Grid item xs={6} sm={4} md={3} key={i} sx={{ display: 'flex' }}>
                <SpeakerCard speaker={s} onEdit={openEdit} onDelete={handleDelete} />
              </Grid>
            ))}
        </Grid>
      )}

      <SpeakerDrawer
        open={drawerOpen}
        speaker={editing}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveSpeaker}
      />
    </Box>
  );
});

export default SpeakersTab;